import { useMemo } from 'react';
import type { CartItem } from '@/hooks/useCart';
import { products } from '@/mocks/products';

interface OrderSummaryProps {
  cart: CartItem[];
  onCheckout: () => void;
}

function formatPrice(amount: number): string {
  return `KSh ${amount.toLocaleString('en-KE')}`;
}

export default function OrderSummary({ cart, onCheckout }: OrderSummaryProps) {
  const { subtotal, deliveryFee, total, savings } = useMemo(() => {
    let sub = 0;
    let save = 0;
    cart.forEach((item) => {
      const product = products.find((p) => p.id === item.productId);
      if (!product) return;
      const price = product.salePrice ?? product.price;
      sub += price * item.quantity;
      if (product.salePrice) {
        save += (product.price - product.salePrice) * item.quantity;
      }
    });
    const delivery = sub >= 5000 ? 0 : 300;
    return {
      subtotal: sub,
      deliveryFee: delivery,
      total: sub + delivery,
      savings: save,
    };
  }, [cart]);

  return (
    <div className="rounded-xl bg-white dark:bg-background-100 border border-background-200/60 p-4 md:p-5 space-y-4 sticky top-36">
      <h3 className="text-lg font-bold text-foreground-950 font-heading">Order Summary</h3>

      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-foreground-600">Subtotal</span>
          <span className="font-medium text-foreground-900">{formatPrice(subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-foreground-600">Delivery Fee</span>
          <span className="font-medium text-foreground-900">
            {deliveryFee === 0 ? (
              <span className="text-green-700">Free</span>
            ) : (
              formatPrice(deliveryFee)
            )}
          </span>
        </div>
        {savings > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-foreground-600">You Save</span>
            <span className="font-medium text-accent-700">-{formatPrice(savings)}</span>
          </div>
        )}
      </div>

      <div className="h-px bg-background-200/70" />

      <div className="flex justify-between">
        <span className="text-base font-bold text-foreground-950">Total</span>
        <span className="text-xl font-bold text-primary-600 font-heading">{formatPrice(total)}</span>
      </div>

      {deliveryFee === 0 && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 p-2.5">
          <i className="ri-truck-line text-green-600 text-sm" />
          <span className="text-xs text-green-700 font-medium">
            Free delivery on orders above KSh 5,000
          </span>
        </div>
      )}

      {deliveryFee > 0 && (
        <div className="flex items-center gap-2 rounded-lg bg-accent-50 p-2.5">
          <i className="ri-shopping-basket-line text-accent-600 text-sm" />
          <span className="text-xs text-accent-800">
            Add KSh {formatPrice(5000 - subtotal).replace('KSh ', '')} more for free delivery
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={onCheckout}
        className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors flex items-center justify-center gap-2"
      >
        <i className="ri-secure-payment-line" />
        Proceed to Checkout
      </button>

      <div className="flex items-center justify-center gap-1 text-xs text-foreground-500">
        <i className="ri-shield-check-line" />
        Secure checkout with M-Pesa & Cards
      </div>
    </div>
  );
}